import React, { useMemo, useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { NavigationItem as NavItemType } from '../types/navigation';

interface TeammateScorecardNavigationItemProps {
  item: NavItemType;
  isSecondPanelCollapsed: boolean;
  expandedItems: string[];
  toggleExpanded: (itemTitle: string) => void;
  hasActiveChild?: (children: NavItemType[]) => boolean;
  isItemActive: (path: string) => boolean;
  navigate: (path: string) => void;
  filterMenuItems: (items: NavItemType[]) => NavItemType[];
}

interface Teammate {
  id: string;
  name: string;
  email: string;
}

const TeammateScorecardNavigationItem = ({
  item,
  isSecondPanelCollapsed,
  expandedItems,
  toggleExpanded,
  hasActiveChild,
  isItemActive,
  navigate,
  filterMenuItems,
}: TeammateScorecardNavigationItemProps) => {
  const [teammates, setTeammates] = useState<Teammate[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Mock data for teammates - replace with actual API call
  useEffect(() => {
    const fetchTeammates = async () => {
      setIsLoading(true);
      try {
        // Mock teammates data
        const mockTeammates: Teammate[] = [
          { id: '1', name: 'Alice Johnson', email: 'alice@company.com' },
          { id: '2', name: 'Bob Smith', email: 'bob@company.com' },
          { id: '3', name: 'Carol Williams', email: 'carol@company.com' },
          { id: '4', name: 'David Brown', email: 'david@company.com' },
        ];
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 500));
        setTeammates(mockTeammates);
      } catch (error) {
        console.error('Error fetching teammates:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (expandedItems.includes(item.title)) {
      fetchTeammates();
    }
  }, [expandedItems, item.title]);

  const teammateNavItems = useMemo(() => {
    return teammates.map(teammate => ({
      title: teammate.name,
      path: `/home/autoqa/teammate-scorecards/${teammate.id}`,
      permissionKey: 'settings.autoqa',
    }));
  }, [teammates]);

  const hasActiveChildItem = useMemo(() => {
    return teammateNavItems.some(navItem => isItemActive(navItem.path));
  }, [teammateNavItems, isItemActive]);

  return (
    <div>
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              className={`w-full flex items-center rounded-lg transition-all duration-300 group ${
                isSecondPanelCollapsed ? 'justify-center p-2' : 'justify-between px-4 py-2'
              } ${
                hasActiveChildItem
                  ? 'bg-primary/5 text-primary shadow-sm hover:shadow-md'
                  : 'hover:bg-primary/5'
              }`}
              onClick={() => toggleExpanded(item.title)}
            >
              <div className={`flex items-center ${isSecondPanelCollapsed ? 'justify-center' : 'gap-3'}`}>
                {item.icon && (
                  <div className="flex items-center justify-center w-5">
                    <item.icon className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
                  </div>
                )}
                {!isSecondPanelCollapsed && (
                  <span className="font-medium transition-colors group-hover:text-primary">
                    {item.title}
                  </span>
                )}
              </div>
              {!isSecondPanelCollapsed && (
                <ChevronRight className={`h-4 w-4 transition-all duration-300 ${
                  expandedItems.includes(item.title) ? 'rotate-90' : ''
                } group-hover:text-primary`} />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            className="z-[60] bg-white shadow-lg"
            sideOffset={12}
          >
            <p>{item.title}</p>
            <p className="text-xs text-muted-foreground">
              Click to {expandedItems.includes(item.title) ? 'collapse' : 'expand'}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      {expandedItems.includes(item.title) && !isSecondPanelCollapsed && (
        <div className="ml-8 mt-1 space-y-1 animate-accordion-down">
          {isLoading ? (
            <div className="px-4 py-2 text-sm text-muted-foreground">
              Loading teammates...
            </div>
          ) : teammateNavItems.length === 0 ? (
            <div className="px-4 py-2 text-sm text-muted-foreground">
              No teammates found
            </div>
          ) : (
            teammateNavItems.map((teammateItem) => (
              <TooltipProvider key={teammateItem.title} delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      className={`w-full flex items-center justify-start text-sm px-4 py-2 rounded-lg transition-all duration-300 group
                        ${isItemActive(teammateItem.path)
                          ? 'bg-primary/5 text-primary shadow-sm hover:shadow-md'
                          : 'hover:bg-primary/5'}`}
                      onClick={() => navigate(teammateItem.path)}
                    >
                      <span className="transition-colors group-hover:text-primary">
                        {teammateItem.title}
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    className="z-[60] bg-white shadow-lg"
                    sideOffset={12}
                  >
                    <p>{teammateItem.title}</p>
                    <p className="text-xs text-muted-foreground">View scorecard</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default TeammateScorecardNavigationItem;